import {
	DataGrid,
	DataGridButton,
	DataGridButtonType,
	DataGridProps,
	useIsFeatureEnabled,
	success,
	error,
	getSubscriptions,
} from '@innovyze/stylovyze';
import { fullAccess } from '@innovyze/shared-utils';
import {
	GridCellParams,
	GridLogicOperator,
	GridRowId,
	useGridApiRef,
} from '@mui/x-data-grid-premium';
import { SelectChangeEvent } from '@mui/material';
import { DateTime } from 'luxon';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { SystemAssetSelectors, Wrapper } from './AssetGrid.styles';
import { generateColumns, generateSpatialColumns } from './utils/columnMapping';
import {
	getGridData,
	getGridDataReset,
	selectGridLoading,
	selectGridPagination,
	selectGridRows,
	selectGridSort,
	selectSelectedAssets,
} from './redux/assetGridSlice';
import {
	selectAssetGridColumnLayout,
	selectSpatialDataColumnLayout,
} from '../../redux/asset/selectors/map';
import { useDispatch, useSelector } from 'react-redux';

import { Asset, Filter } from './utils/types';
import { AssetLinkProps } from './components/AssetLink';
import { AssetTypeSelect } from './components/AssetSelectors';
import { SelectSystemAsset } from './components/AssetSelectors';
import { ShowInMenu } from './utils/getActions';
import { SystemTypeSelect } from './components/AssetSelectors';
import { initialState as assetInitialState } from './redux/assetGridSlice';
import { convertSortModel } from './utils/grid';
import { getSpatialMetadata } from '../../redux/risk/actions';
import { useCustomSelection } from './hooks/useCustomSelection';
import { useFilters } from '../useFilters';
import { setSelectedAssetViewAction } from '../useFilters/filterSlice';
import { useFilterModel } from './hooks/useFilterModel';
import { useGlobalization } from '../../../../i18n/useGlobalization';
import { useGridViewManager } from './hooks/useGridViewManager';
import { useShallowEqlEffect } from './hooks/useShallowEqlEffect';
import { FilterSaveDialog } from './components/FilterSaveDialog';
import { FilterDrawer } from './components/FilterDrawer';
import { getAllAssetsSchemaAction } from '@innovyze/lib_asset_schema_store';
import * as FilterService from '../../services/filter';
import { AxiosError } from 'axios';

// Cell fields whose clicks must NOT toggle the map-marker row selection
// (the exclude-filter checkbox column and the row actions column).
const NON_SELECT_FIELDS = ['custom_selected', 'actions'];

export interface AssetGridProps {
	onViewDetails?: AssetLinkProps['onViewDetails'];
	onSelectedAssetsChange?: (selectedAssets: Asset[]) => void;
	actionMenuItems?: ShowInMenu;
}

export const AssetGrid = ({
	onViewDetails,
	onSelectedAssetsChange,
	actionMenuItems = [],
}: AssetGridProps): JSX.Element => {
	const dispatch = useDispatch();

	const { t } = useGlobalization();
	const isSpatialFilterEnabled = Boolean(
		useIsFeatureEnabled(
			'i-360-release-146545-add-filter-to-spatial-data-in-data-grid',
		),
	);

	const gridSelectFeature = Boolean(
		useIsFeatureEnabled('i-360-release-145673-data-grid-select'),
	);

	const saveFilterFeature = !!useIsFeatureEnabled(
		'info-360-assets-save-filter',
	);

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const [
		isViewSelected,
		{ dataType, layerName },
		setDataType,
		setLayerName,
		{ systemTypes, assetTypes, systemSchema, assetSchema, loadSystemSchema, loadAssetSchema },
	] = useGridViewManager();

	const isSpatialLayerSelected = dataType === 'SpatialData';

	const loading = useSelector(selectGridLoading);
	const rows = useSelector(selectGridRows);
	const pagination = useSelector(selectGridPagination);
	const sort = useSelector(selectGridSort);
	const filterButton = useRef(null);
	const apiRef = useGridApiRef();
	const { excludedFilter, filterCount } = useFilters();
	const [additionalFilterProps, setAdditionalFilterProps] = useState<
		Record<string, unknown>
	>({});

	const { columns, visibility } = useSelector(state => {
		if (isSpatialLayerSelected) {
			return selectSpatialDataColumnLayout(state, layerName);
		}

		return selectAssetGridColumnLayout(state, layerName);
	});

	const [paginationModel, setPaginationModel] = useState({
		page: (pagination?.current ?? 1) - 1,
		pageSize: pagination?.limit ?? 10,
	});
	const [sortModel, setSortModel] = useState(sort);
	const gridSortModel = useMemo(() => convertSortModel(sortModel), [
		sortModel,
	]);
	const [
		filterModel,
		setFilterModel,
		filterModelForServer,
		setFilterModelFromServer,
	] = useFilterModel(columns ?? []);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState(
		visibility,
	);

	const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
	const selectedAssets = useSelector(state =>
		selectSelectedAssets(state, selectionModel, { dataType, layerName }),
	);

	// Toggle row selection on cell click. Clicking a row adds it to the selection
	// and drops a map pin; re-clicking a selected row removes just that row and its
	// pin (I360P-1195 — deselecting the last row clears all pins). Clicks on the
	// exclude-filter checkbox and actions cells must not move the selection.
	const onCellClick = useCallback((params: GridCellParams) => {
		if (NON_SELECT_FIELDS.includes(params.field)) return;
		setSelectionModel(prev =>
			prev.includes(params.id)
				? prev.filter(id => id !== params.id)
				: [...prev, params.id],
		);
	}, []);
	const [savedFilters, setSavedFilters] = useState<Filter[]>([]);

	const customSelection = useCustomSelection(paginationModel.pageSize);

	const onSortModelChange: DataGridProps['onSortModelChange'] = useCallback(
		model => {
			customSelection.resetSelection();
			setSelectionModel([]);
			if (model[0]?.field && model[0]?.sort) {
				setSortModel({
					sort: model[0]?.field,
					sortDescending: model[0]?.sort === 'desc',
				});
			} else {
				const { sort, sortDescending } = assetInitialState;
				setSortModel({
					sort,
					sortDescending,
				});
			}
		},
		[],
	);

	const onPageModelChange: DataGridProps['onPaginationModelChange'] = useCallback(
		model => {
			customSelection.resetSelection();
			setSelectionModel([]);
			setPaginationModel(oldModel => ({
				...oldModel,
				...model,
				// reset pagination to 1st page if the pagesize has changed
				page: oldModel.pageSize !== model.pageSize ? 0 : model.page,
			}));
		},
		[],
	);

	const onColumnVisibilityChange: DataGridProps['onColumnVisibilityModelChange'] = useCallback(
		model => {
			setColumnVisibilityModel(model);
		},
		[],
	);

	// Clear the row selection (and its map pins) when the filter changes, since
	// server-side pagination only resolves the current result set to pins.
	const onFilterModelChange: DataGridProps['onFilterModelChange'] = useCallback(
		model => {
			setSelectionModel([]);
			setFilterModel(model);
		},
		[setFilterModel],
	);

	// Use a serialized key to prevent re-running when visibility content is the
	// same but the selector returns a new object reference (e.g. after schema loads).
	const visibilityKey = JSON.stringify(visibility);
	useEffect(() => {
		setColumnVisibilityModel(visibility);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibilityKey]);

	const resetFilters = () => {
		// Guard: only dispatch if there are items to clear, to prevent creating a
		// new rawFilter reference in Redux when the filter is already empty.
		if (filterModel.items.length > 0) {
			const model = { ...filterModel, items: [] };
			setFilterModel(model);
		}
	};

	const onSystemTypeChange = (event: SelectChangeEvent<string>) => {
		const newSystemType = event.target.value;
		setDataType(newSystemType);
		resetFilters();
	};

	useEffect(() => {
		if (dataType) {
			setAdditionalFilterProps({ systemType: dataType });
		}
	}, [dataType]);

	const onAssetTypeChange = (event: SelectChangeEvent<string>) => {
		const newAssetType = event.target.value;
		setLayerName(newAssetType);
		setPaginationModel(model => ({
			...model,
			page: 0,
		}));
		resetFilters();
		setAdditionalFilterProps(state => ({
			...state,
			assetType: newAssetType,
		}));
	};

	const showFilterPanel = () => {
		apiRef.current.showFilterPanel();
	};

	const fetchData = () => {
		if (isViewSelected) {
			dispatch(setSelectedAssetViewAction({ systemType: dataType, assetType: layerName }));
			dispatch(
				getGridData({
					assetType: layerName,
					systemType: dataType,
					limit: paginationModel.pageSize,
					offset: paginationModel.page + 1,
					sort: sortModel.sort,
					sortDescending: sortModel.sortDescending,
					filterModel: filterModelForServer,
					exclude: excludedFilter,
				}),
			);
		} else {
			dispatch(getGridDataReset());
		}
	};

	useEffect(() => {
		fetchData();
	}, [
		paginationModel.page,
		paginationModel.pageSize,
		dataType,
		layerName,
		sortModel.sort,
		sortModel.sortDescending,
		JSON.stringify(filterModelForServer),
		JSON.stringify(excludedFilter),
	]);

	const saveFilter = async (name: string) => {
		try {
			// eslint-disable-next-line quotes
			const now = DateTime.now().toFormat("ccc d LLL 'at' HH:mm");
			const payload: Filter = {
				name: name || `Filter saved on ${now}`,
				type: filterModel.logicOperator ?? GridLogicOperator.And,
				assetType: layerName,
				systemType: dataType,
				filters: filterModelForServer.items.map(f => ({
					field: f.field,
					operator: f.operator,
					value: f.value,
				})),
			};
			const res = await FilterService.saveFilter(payload);
			if (res.data?._id) {
				setSavedFilters([res.data, ...savedFilters]);
				dispatch(success('Filter saved successfully'));
			} else {
				throw new Error();
			}
		} catch (e) {
			// Pass server error message (if available)
			const serverError = e as AxiosError;
			const errorMessage = (serverError.response?.data as { msg?: string })?.msg;
			if (errorMessage) throw new Error(errorMessage);
			else dispatch(error('Error saving filter'));
		}
	};

	const applyFilter = async (filter: Filter) => {
		const model = {
			...filterModel,
			logicOperator: filter.type,
			items: filter.filters,
		};
		setFilterModelFromServer(model);
		await new Promise(resolve => setTimeout(resolve, 500));
		dispatch(success('Filter model applied'));
	};

	useEffect(() => {
		if (layerName && saveFilterFeature) {
			FilterService.loadFilters({ systemType: dataType, assetType: layerName })
				.then(res => setSavedFilters(res.data))
				.catch(() => dispatch(error('Error getting saved filters')));
		}
	}, [layerName]);

	useShallowEqlEffect(() => {
		onSelectedAssetsChange?.(selectedAssets);
	}, [selectedAssets]);

	useEffect(() => {
		dispatch(getAllAssetsSchemaAction());
		dispatch(getSpatialMetadata());
	}, []);

	return (
		<Wrapper>
			<SystemAssetSelectors>
				<SystemTypeSelect
					systemType={dataType}
					onSystemTypeChange={onSystemTypeChange}
					systemTypes={systemTypes}
					systemSchema={systemSchema}
					loadSystemSchema={loadSystemSchema}
					dataCy="system-type-select-top-bar"
					topBar
				/>
				<AssetTypeSelect
					systemType={dataType}
					assetType={layerName}
					onAssetTypeChange={onAssetTypeChange}
					assetTypes={assetTypes}
					assetSchema={assetSchema}
					loadAssetSchema={loadAssetSchema}
					dataCy="asset-type-select-top-bar"
					topBar
				/>
				{saveFilterFeature && (
					<FilterSaveDialog onSave={saveFilter}>
						<DataGridButton
							type={DataGridButtonType.SAVE}
							disabled={!(hasFullAccess && filterCount)}
							data-cy="saveButton"
						/>
					</FilterSaveDialog>
				)}
				{saveFilterFeature && (
					<FilterDrawer
						onSelect={applyFilter}
						assetType={layerName}
						filters={savedFilters}>
						<DataGridButton
							type={DataGridButtonType.SAVED_FILTERS}
							disabled={
								!isViewSelected ||
								(isSpatialLayerSelected &&
									!isSpatialFilterEnabled)
							}
							data-cy="savedFilters"
						/>
					</FilterDrawer>
				)}
				<DataGridButton
					type={DataGridButtonType.FILTER}
					disabled={
						!isViewSelected ||
						(isSpatialLayerSelected && !isSpatialFilterEnabled)
					}
					data-cy="filterButton"
					onClick={showFilterPanel}
					ref={filterButton}
					style={{
						marginRight: '0.5rem',
					}}
				/>
				<DataGridButton
					type={DataGridButtonType.REFRESH}
					disabled={!isViewSelected}
					data-cy="refreshButton"
					onClick={fetchData}
				/>
			</SystemAssetSelectors>
			{!isViewSelected && (
				<SelectSystemAsset
					assetType={layerName}
					systemType={dataType}
					onSystemTypeChange={onSystemTypeChange}
					onAssetTypeChange={onAssetTypeChange}
					systemTypes={systemTypes}
					assetTypes={assetTypes}
					systemSchema={systemSchema}
					assetSchema={assetSchema}
					loadSystemSchema={loadSystemSchema}
					loadAssetSchema={loadAssetSchema}
				/>
			)}
			{isViewSelected && (
				<DataGrid
					apiRef={apiRef}
					columns={
						isSpatialLayerSelected
							? generateSpatialColumns(
								columns,
								t,
								gridSelectFeature,
								customSelection,
								isSpatialFilterEnabled
									? actionMenuItems
									: [],
							)
							: generateColumns(
								columns,
								layerName,
								dataType,
								t,
								onViewDetails,
								actionMenuItems,
								gridSelectFeature,
								customSelection,
							)
					}
					loading={!rows?.length && loading}
					rows={rows ?? []}
					getRowId={row => row._id}
					height="calc(100% - 75px)"
					autoHeight={false}
					pageSizeOptions={[5, 10, 15, 25, 50, 100]}
					pagination
					paginationMode="server"
					paginationModel={paginationModel}
					onPaginationModelChange={onPageModelChange}
					rowCount={pagination?.total ?? 0}
					rowSelectionModel={selectionModel}
					disableRowSelectionOnClick
					onCellClick={onCellClick}
					footerHelper={t('{{from}}-{{to}} of {{total}}', {
						from: ((pagination?.current ?? 1) - 1) * (pagination?.limit ?? 10) + 1,
						to:
							pagination?.current == pagination?.pages
								? pagination?.total
								: (pagination?.current ?? 1) * (pagination?.limit ?? 10),
						total: pagination?.total,
					})}
					sortingMode="server"
					sortModel={gridSortModel}
					onSortModelChange={onSortModelChange}
					filterMode="server"
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={onColumnVisibilityChange}
					slotProps={{
						panel: {
							anchorEl: filterButton.current,
							placement: 'left',
						},
					}}
					data-cy="asset-grid"
				/>
			)}
		</Wrapper>
	);
};
